import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { getUserSessionScopeSuffix } from '@/utils/userSessionScope';

export type MessageRole = 'user' | 'assistant';

/** 消息附件（MinIO 临时路径对应的访问 URL） */
export interface ChatMessageAttachment {
  name: string;
  /** 后端转换后的内部文件名，仅供解析服务识别格式，界面继续展示 name */
  processingName?: string;
  processingObjectKey?: string;
  processingUrl?: string;
  url: string;
  type: string; // 'image' | 'document'
  /** MinIO 对象键（上传时由后端返回，避免前端解 JWT 损坏中文文件名） */
  objectKey?: string;
}

/** 用户消息中附带的智能文档 @ 引用（用于会话区展示全名） */
export interface ChatMessageAtToken {
  kind: string;
  name: string;
  label?: string;
  refId?: string;
}

export interface ChatMessage {
  id: string;
  role: MessageRole;
  content: string;
  createdAt: number;
  attachments?: ChatMessageAttachment[];
  thought?: string;
  structuredResult?: Record<string, unknown> | null;
  /** 智能文档 @技能/@模板/@项目 引用快照 */
  atTokens?: ChatMessageAtToken[];
}

const CONVERSATION_KEY_PREFIX = 'aipd_ai_assistant_';
const MAX_STORED_MESSAGES = 80;
const MAX_MINIMAL_MESSAGES = 40;
const MAX_CONTENT_CHARS = 12000;
const MAX_THOUGHT_CHARS = 4000;

function scenarioStorageKey(scenario: string): string {
  const s = scenario && scenario.trim() ? scenario.trim() : 'project_assistance';
  return `${CONVERSATION_KEY_PREFIX}${getUserSessionScopeSuffix()}_scenario_${s}`;
}

function sessionStorageKey(scenario: string, sessionId: string): string {
  const s = scenario && scenario.trim() ? scenario.trim() : 'project_assistance';
  const sid = sessionId?.trim() || '_default';
  return `${CONVERSATION_KEY_PREFIX}${getUserSessionScopeSuffix()}_scenario_${s}_sid_${sid}`;
}

function resolveStorageKey(scenario: string, sessionId?: string): string {
  if (sessionId?.trim()) return sessionStorageKey(scenario, sessionId.trim());
  return scenarioStorageKey(scenario);
}

function loadFromStorage(key: string): ChatMessage[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const arr = JSON.parse(raw) as ChatMessage[];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function truncateText(text: string | undefined, maxLen: number): string | undefined {
  if (typeof text !== 'string') return text;
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen)}…`;
}

function compactMessageForStorage(message: ChatMessage): ChatMessage {
  const content = truncateText(message.content, MAX_CONTENT_CHARS) ?? message.content;
  const thought = truncateText(message.thought, MAX_THOUGHT_CHARS);
  const atTokens =
    message.atTokens?.length
      ? message.atTokens.map((t) => ({
          kind: t.kind,
          name: t.name,
          label: t.label,
          refId: t.refId,
        }))
      : undefined;
  return {
    ...message,
    content,
    thought,
    atTokens,
  };
}

function minimalMessageForStorage(message: ChatMessage): ChatMessage {
  return {
    id: message.id,
    role: message.role,
    content: truncateText(message.content, 6000) ?? message.content,
    createdAt: message.createdAt,
    structuredResult: message.structuredResult ?? null,
  };
}

function pruneOldAssistantSessions(currentKey: string) {
  try {
    const scope = getUserSessionScopeSuffix();
    const prefix = `${CONVERSATION_KEY_PREFIX}${scope}_scenario_`;
    const keys = Object.keys(localStorage)
      .filter((k) => k.startsWith(prefix) && k.includes('_sid_') && k !== currentKey)
      .sort((a, b) => {
        const ta = Number(localStorage.getItem(`${a}__ts`) || 0);
        const tb = Number(localStorage.getItem(`${b}__ts`) || 0);
        return ta - tb;
      });
    const overflow = keys.length - 24;
    for (let i = 0; i < overflow; i += 1) {
      localStorage.removeItem(keys[i]);
      localStorage.removeItem(`${keys[i]}__ts`);
    }
  } catch {
    /* ignore quota / access errors during prune */
  }
}

function saveToStorage(key: string, messages: ChatMessage[]) {
  const payload = messages.slice(-MAX_STORED_MESSAGES).map(compactMessageForStorage);
  try {
    localStorage.setItem(key, JSON.stringify(payload));
    localStorage.setItem(`${key}__ts`, String(Date.now()));
    return;
  } catch (e) {
    const isQuota =
      e instanceof DOMException
      && (e.name === 'QuotaExceededError' || e.code === 22);
    if (!isQuota) {
      console.error('保存会话失败', e);
      return;
    }
  }

  pruneOldAssistantSessions(key);

  try {
    const minimal = messages.slice(-MAX_MINIMAL_MESSAGES).map(minimalMessageForStorage);
    localStorage.setItem(key, JSON.stringify(minimal));
    localStorage.setItem(`${key}__ts`, String(Date.now()));
    return;
  } catch {
    /* fall through */
  }

  try {
    const tiny: ChatMessage[] = messages.slice(-20).map((m: ChatMessage) => ({
      id: m.id,
      role: m.role,
      content: truncateText(m.content, 2000) ?? m.content,
      createdAt: m.createdAt,
      structuredResult: m.structuredResult ?? null,
    }));
    localStorage.setItem(key, JSON.stringify(tiny));
    localStorage.setItem(`${key}__ts`, String(Date.now()));
  } catch (e2) {
    console.error('保存会话失败', e2);
  }
}

export const useAiAssistantStore = defineStore('aiAssistant', () => {
  const conversations = ref<Record<string, ChatMessage[]>>({});

  function loadMessages(scenario: string, sessionId?: string): ChatMessage[] {
    const key = resolveStorageKey(scenario, sessionId);
    if (conversations.value[key]) return conversations.value[key];
    let loaded = loadFromStorage(key);
    if (!loaded.length && sessionId?.trim()) {
      const legacyKey = scenarioStorageKey(scenario);
      const legacy = loadFromStorage(legacyKey);
      if (legacy.length) {
        loaded = legacy;
        saveToStorage(key, legacy);
        // 迁移后清除旧key，防止污染新session
        try { localStorage.removeItem(legacyKey); localStorage.removeItem(legacyKey + "__ts"); } catch { /* ignore */ }
      }
    }
    conversations.value[key] = loaded;
    return loaded;
  }

  /** 获取消息；传入 sessionId 时按会话隔离，否则兼容旧版场景级存储 */
  function getMessages(scenario: string, sessionId?: string): ChatMessage[] {
    return loadMessages(scenario, sessionId);
  }

  function addMessage(
    scenario: string,
    role: MessageRole,
    content: string,
    attachments?: ChatMessageAttachment[],
    thought?: string,
    structuredResult?: Record<string, unknown> | null,
  ): ChatMessage;
  function addMessage(
    scenario: string,
    sessionId: string,
    role: MessageRole,
    content: string,
    attachments?: ChatMessageAttachment[],
    thought?: string,
    structuredResult?: Record<string, unknown> | null,
  ): ChatMessage;
  function addMessage(
    scenario: string,
    roleOrSessionId: MessageRole | string,
    contentOrRole: string | MessageRole,
    attachmentsOrContent?: ChatMessageAttachment[] | string,
    thoughtOrAttachments?: string | ChatMessageAttachment[],
    structuredOrThought?: Record<string, unknown> | null | string,
    structuredResult?: Record<string, unknown> | null,
  ): ChatMessage {
    let sessionId: string | undefined;
    let role: MessageRole;
    let content: string;
    let attachments: ChatMessageAttachment[] | undefined;
    let thought: string | undefined;
    let structured: Record<string, unknown> | null | undefined;

    if (roleOrSessionId === 'user' || roleOrSessionId === 'assistant') {
      role = roleOrSessionId;
      content = String(contentOrRole ?? '');
      if (Array.isArray(attachmentsOrContent)) attachments = attachmentsOrContent;
      if (typeof thoughtOrAttachments === 'string') thought = thoughtOrAttachments;
      if (typeof structuredOrThought === 'object') structured = structuredOrThought ?? null;
      if (structuredResult !== undefined) structured = structuredResult;
    } else {
      sessionId = roleOrSessionId;
      role = contentOrRole as MessageRole;
      content = String(attachmentsOrContent ?? '');
      if (Array.isArray(thoughtOrAttachments)) attachments = thoughtOrAttachments;
      if (typeof structuredOrThought === 'string') thought = structuredOrThought;
      else if (typeof structuredOrThought === 'object') structured = structuredOrThought ?? null;
      if (structuredResult !== undefined) structured = structuredResult;
    }

    const key = resolveStorageKey(scenario, sessionId);
    const list = conversations.value[key] ?? loadFromStorage(key);
    const attachmentsCopy =
      attachments?.length ?
        attachments.map((a) => ({
          name: a.name,
          processingName: a.processingName,
          processingObjectKey: a.processingObjectKey,
          processingUrl: a.processingUrl,
          url: a.url,
          type: a.type,
          objectKey: a.objectKey,
        }))
      : undefined;
    const msg: ChatMessage = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      role,
      content,
      createdAt: Date.now(),
      attachments: attachmentsCopy,
      thought,
      structuredResult: structured ?? null,
    };
    const next = [...list, msg];
    conversations.value[key] = next;
    saveToStorage(key, next);
    return msg;
  }

  function setMessages(scenario: string, messages: ChatMessage[], sessionId?: string) {
    const key = resolveStorageKey(scenario, sessionId);
    conversations.value[key] = messages;
    saveToStorage(key, messages);
  }

  function clearConversation(scenario: string, sessionId?: string) {
    const key = resolveStorageKey(scenario, sessionId);
    conversations.value[key] = [];
    saveToStorage(key, []);
  }

  function updateMessage(
    scenario: string,
    id: string,
    patch: Partial<ChatMessage>,
    sessionId?: string,
  ) {
    const key = resolveStorageKey(scenario, sessionId);
    const list = conversations.value[key] ?? loadFromStorage(key);
    const idx = list.findIndex((m) => m.id === id);
    if (idx === -1) return;
    const updated: ChatMessage = { ...list[idx], ...patch };
    const next = [...list];
    next[idx] = updated;
    conversations.value[key] = next;
    saveToStorage(key, next);
  }

  const currentScenario = ref<string>('project_assistance');
  const assistantTypingPreviewByMessageId = ref<Record<string, string>>({});

  function setAssistantTypingPreview(messageId: string, partial: string) {
    assistantTypingPreviewByMessageId.value = {
      ...assistantTypingPreviewByMessageId.value,
      [messageId]: partial,
    };
  }

  function clearAssistantTypingPreview(messageId: string) {
    if (!(messageId in assistantTypingPreviewByMessageId.value)) return;
    const next = { ...assistantTypingPreviewByMessageId.value };
    delete next[messageId];
    assistantTypingPreviewByMessageId.value = next;
  }

  const currentMessages = computed(() => loadMessages(currentScenario.value));

  function resetForUserSwitch() {
    conversations.value = {};
    currentScenario.value = 'project_assistance';
    assistantTypingPreviewByMessageId.value = {};
  }

  return {
    getMessages,
    addMessage,
    setMessages,
    clearConversation,
    updateMessage,
    currentScenario,
    currentMessages,
    assistantTypingPreviewByMessageId,
    setAssistantTypingPreview,
    clearAssistantTypingPreview,
    resetForUserSwitch,
  };
});
